import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import styles from './AutocompleteSelectFilter.module.scss';

const getValue = (item) => {
  if (item && typeof item === 'object' && item.value !== undefined) return item.value;
  return item;
};

const getLabel = (item, options = []) => {
  if (item && typeof item === 'object' && item.label) return item.label;
  const match = options.find((option) => option.value === getValue(item));
  return match?.label ?? String(getValue(item) ?? '');
};

const AutocompleteSelectFilter = ({
  options = [],
  selectedValues,
  onChange,
  label = '',
  placeholder = '',
  isMultiple = true,
  isDisabled = false,
  direction = 'rtl',
  open: openProp = undefined,
  onOpenChange = undefined,
  triggerRef = null,
  onSelectConfirm = null,
  /** Render menu in a portal so overflow parents cannot clip it. */
  portalMenu = false,
  /** Override portal menu stacking when used inside a high-z-index dialog. */
  portalZIndex = 2100,
  /** Cap dropdown list height (px). Defaults: searchable 300, otherwise 240. */
  menuMaxHeight: menuMaxHeightProp = undefined,
  /** Type-to-filter options in the dropdown (like AutocompleteSelect). */
  searchable = false,
  /** Show X to clear the single selected value (Projects-style). */
  clearable = false,
  /** Optional group header fn — headers are view-only (not selectable). */
  groupBy = null,
  /** Optional action shown under NoOptions (e.g. Add Customer). */
  emptyAction = null,
  /** Show every selected chip instead of one chip plus "+N more". */
  showAllSelected = false,
}) => {
  const { t } = useTranslation();
  const isOpenControlled = openProp !== undefined && typeof onOpenChange === 'function';
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = isOpenControlled ? !!openProp : uncontrolledOpen;
  const setOpen = (next) => {
    const value = typeof next === 'function' ? next(open) : next;
    if (isOpenControlled) onOpenChange(value);
    else setUncontrolledOpen(value);
  };

  const rootRef = useRef(null);
  const listRef = useRef(null);
  const searchInputRef = useRef(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [portalStyle, setPortalStyle] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  const selectedList = isMultiple
    ? (Array.isArray(selectedValues) ? selectedValues : [])
    : [];

  const isOptionSelected = (option) => {
    if (isMultiple) {
      return selectedList.some((item) => String(getValue(item)) === String(option.value));
    }
    if (selectedValues == null) return false;
    return String(getValue(selectedValues)) === String(option.value);
  };

  const uniqueSelected = selectedList.reduce((unique, item) => {
    const value = getValue(item);
    if (!unique.some((entry) => getValue(entry) === value)) unique.push(item);
    return unique;
  }, []);

  const displayedSelected = showAllSelected ? uniqueSelected : uniqueSelected.slice(0, 1);
  const remainingCount = uniqueSelected.length - displayedSelected.length;
  const allSelected = isMultiple && options.length > 0 && uniqueSelected.length === options.length;
  const hasValue = isMultiple
    ? uniqueSelected.length > 0
    : selectedValues !== null && selectedValues !== undefined;
  const emptyText = hasValue ? '' : (placeholder || label);

  const filteredOptions = useMemo(() => {
    if (!searchable || !searchQuery.trim()) return options;
    const term = searchQuery.trim().toLowerCase();
    return options.filter((option) => {
      if (!option) return false;
      const labelText = String(option.label ?? '').toLowerCase();
      const valueText = String(option.value ?? '').toLowerCase();
      const nameText = String(option.name ?? option.Name ?? '').toLowerCase();
      const makatText = String(option.makat ?? option.Makat ?? '').toLowerCase();
      const cardIdText = String(option.cardId ?? option.accountCodeId ?? '').toLowerCase();
      const accountCaseIdText = String(option.accountCaseId ?? '').toLowerCase();
      const groupText = groupBy ? String(groupBy(option) ?? '').toLowerCase() : '';
      return (
        labelText.includes(term) ||
        valueText.includes(term) ||
        nameText.includes(term) ||
        (makatText !== '' && makatText.includes(term)) ||
        (cardIdText !== '' && cardIdText.includes(term)) ||
        (accountCaseIdText !== '' && accountCaseIdText.includes(term)) ||
        groupText.includes(term)
      );
    });
  }, [options, searchable, searchQuery, groupBy]);

  const groupedOptions = useMemo(() => {
    if (typeof groupBy !== 'function') {
      return [{ group: null, options: filteredOptions }];
    }
    const map = new Map();
    filteredOptions.forEach((option) => {
      const key = groupBy(option) || '';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(option);
    });
    return Array.from(map.entries()).map(([group, groupOptions]) => ({
      group,
      options: groupOptions,
    }));
  }, [filteredOptions, groupBy]);

  const configuredMenuMaxHeight = menuMaxHeightProp ?? (searchable ? 300 : 240);

  useLayoutEffect(() => {
    if (!open || !portalMenu || !rootRef.current) {
      setPortalStyle(null);
      return undefined;
    }

    const updatePosition = () => {
      if (!rootRef.current) return;
      const rect = rootRef.current.getBoundingClientRect();
      const gap = 8;
      const spaceBelow = window.innerHeight - rect.bottom - gap;
      const spaceAbove = rect.top - gap;
      const openUp = spaceBelow < Math.min(configuredMenuMaxHeight, 160);
      const available = openUp ? spaceAbove : spaceBelow;
      const menuMaxHeight = Math.max(120, Math.min(configuredMenuMaxHeight, available));
      const width = rect.width;

      setPortalStyle({
        position: 'fixed',
        zIndex: portalZIndex,
        width,
        maxHeight: menuMaxHeight,
        left: direction === 'rtl' ? rect.right - width : rect.left,
        right: 'auto',
        top: openUp ? 'auto' : rect.bottom + gap,
        bottom: openUp ? window.innerHeight - rect.top + gap : 'auto',
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, portalMenu, portalZIndex, direction, options.length, searchable, searchQuery, configuredMenuMaxHeight]);

  useEffect(() => {
    if (!open) {
      setHighlightedIndex(-1);
      setSearchQuery('');
      setIsTyping(false);
      return undefined;
    }

    const selectedIdx = filteredOptions.findIndex((option) => isOptionSelected(option));
    setHighlightedIndex(selectedIdx >= 0 ? selectedIdx : (filteredOptions.length ? 0 : -1));

    let focusTimer;
    if (searchable) {
      focusTimer = window.setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }

    const handlePointerDown = (event) => {
      const inRoot = rootRef.current?.contains(event.target);
      const inList = listRef.current?.contains(event.target);
      if (!inRoot && !inList) setOpen(false);
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      if (focusTimer) window.clearTimeout(focusTimer);
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, searchable]);

  useEffect(() => {
    if (!open) return;
    const selectedIdx = filteredOptions.findIndex((option) => isOptionSelected(option));
    setHighlightedIndex(selectedIdx >= 0 ? selectedIdx : (filteredOptions.length ? 0 : -1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredOptions, open]);

  const emitChange = (nextValue) => {
    onChange?.(null, nextValue);
  };

  const toggleOpen = () => {
    if (isDisabled) return;
    setOpen((current) => !current);
  };

  const toggleOption = (option) => {
    if (isDisabled) return;

    if (!isMultiple) {
      emitChange(option);
      setIsTyping(false);
      setSearchQuery('');
      setOpen(false);
      onSelectConfirm?.(option);
      return;
    }

    if (isOptionSelected(option)) {
      emitChange(selectedList.filter((item) => getValue(item) !== option.value));
    } else {
      emitChange([...selectedList, option]);
    }
    setIsTyping(false);
    setSearchQuery('');
  };

  const removeSelected = (event, value) => {
    event.stopPropagation();
    if (isDisabled || !isMultiple) return;
    emitChange(selectedList.filter((item) => getValue(item) !== value));
  };

  const clearAll = (event) => {
    event.stopPropagation();
    if (isDisabled) return;
    if (!isMultiple) {
      emitChange(null);
      setIsTyping(false);
      setSearchQuery('');
      // Keep menu open after clear so the user can pick another option immediately.
      if (searchable) {
        setOpen(true);
        window.setTimeout(() => searchInputRef.current?.focus(), 0);
      } else {
        setOpen(false);
      }
      return;
    }
    emitChange([]);
    setIsTyping(false);
    setSearchQuery('');
  };

  const toggleAll = () => {
    if (isDisabled || !isMultiple) return;
    emitChange(allSelected ? [] : [...options]);
    setIsTyping(false);
    setSearchQuery('');
  };

  const singleLabel = !isMultiple && selectedValues
    ? getLabel(selectedValues, options)
    : emptyText;

  const triggerInputValue = isTyping
    ? searchQuery
    : (!isMultiple && hasValue ? singleLabel : '');

  const handleTriggerInputChange = (event) => {
    const next = event.target.value;
    setIsTyping(true);
    setSearchQuery(next);
    if (!open) setOpen(true);
  };

  const handleTriggerInputFocus = () => {
    if (isDisabled) return;
    if (!open) setOpen(true);
    if (!isMultiple && hasValue && !isTyping) {
      window.setTimeout(() => searchInputRef.current?.select(), 0);
    }
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      setHighlightedIndex((current) => {
        if (!filteredOptions.length) return -1;
        if (current < 0) return 0;
        if (event.key === 'ArrowDown') return (current + 1) % filteredOptions.length;
        return (current - 1 + filteredOptions.length) % filteredOptions.length;
      });
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      const option = highlightedIndex >= 0
        ? filteredOptions[highlightedIndex]
        : filteredOptions[0];
      if (option) toggleOption(option);
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
    }
  };

  const handleTriggerKeyDown = (event) => {
    if (isDisabled || searchable) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      setHighlightedIndex((current) => {
        if (!filteredOptions.length) return -1;
        if (current < 0) return 0;
        if (event.key === 'ArrowDown') return (current + 1) % filteredOptions.length;
        return (current - 1 + filteredOptions.length) % filteredOptions.length;
      });
      return;
    }

    if (event.key !== 'Enter' || event.shiftKey) return;

    event.preventDefault();

    if (!open) {
      setOpen(true);
      return;
    }

    if (!isMultiple) {
      const option = highlightedIndex >= 0
        ? filteredOptions[highlightedIndex]
        : filteredOptions.find((item) => isOptionSelected(item)) || filteredOptions[0];
      if (option) {
        emitChange(option);
        setOpen(false);
        onSelectConfirm?.(option);
      } else {
        setOpen(false);
        onSelectConfirm?.(selectedValues || null);
      }
      return;
    }

    setOpen(false);
    onSelectConfirm?.(null);
  };

  const listContent = open && !isDisabled ? (
    <div
      ref={listRef}
      className={`${styles.list}${searchable ? ` ${styles.listSearchable}` : ''}`}
      role="listbox"
      dir={direction}
      style={
        portalMenu && portalStyle
          ? portalStyle
          : { maxHeight: configuredMenuMaxHeight }
      }
    >
      {isMultiple && filteredOptions.length > 0 && (
        <button type="button" className={styles.selectAll} onClick={toggleAll}>
          {allSelected ? t('DeselectAll') : t('SelectAll')}
        </button>
      )}
      {filteredOptions.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyStateText}>
            {t('NoOptions') || t('NoResults') || 'No results'}
          </div>
          {emptyAction?.label && typeof emptyAction.onClick === 'function' ? (
            <button
              type="button"
              className={styles.emptyAction}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setOpen(false);
                emptyAction.onClick(event);
              }}
            >
              {emptyAction.label}
            </button>
          ) : null}
        </div>
      ) : (
        (() => {
          let optionIndex = 0;
          return groupedOptions.map(({ group, options: groupOptions }) => (
            <React.Fragment key={group == null || group === '' ? '__ungrouped__' : String(group)}>
              {group != null && group !== '' ? (
                <div className={styles.groupHeader} role="presentation">
                  {group}
                </div>
              ) : null}
              {groupOptions.map((option) => {
                const index = optionIndex;
                optionIndex += 1;
                const selected = isOptionSelected(option);
                const highlighted = index === highlightedIndex;
                const isAddOption = option?.kind === 'add';
                return (
                    <button
                    type="button"
                    key={`${String(option.value)}-${index}`}
                    className={[
                      styles.option,
                      selected ? styles.optionSelected : '',
                      highlighted ? styles.optionHighlighted || '' : '',
                      isAddOption ? styles.optionAdd : '',
                    ].filter(Boolean).join(' ')}
                    onClick={() => toggleOption(option)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    aria-selected={selected}
                  >
                    <span className={styles.optionLabel}>
                      {isAddOption ? (
                        <span className={styles.addIcon} aria-hidden="true">+</span>
                      ) : null}
                      {option.label || getLabel(option, options)}
                    </span>
                    {isMultiple && selected && (
                      <svg className={styles.check} width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                        <path
                          d="M13.5 4.5L6 12L2.5 8.5"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </button>
                );
              })}
            </React.Fragment>
          ));
        })()
      )}
    </div>
  ) : null;

  return (
    <div
      ref={rootRef}
      className={[
        styles.wrap,
        direction === 'rtl' ? styles.rtl : styles.ltr,
        open ? styles.selectOpen : '',
      ].filter(Boolean).join(' ')}
    >
      <div
        className={[
          styles.select,
          open ? styles.selectOpen : '',
          isDisabled ? styles.disabled : '',
        ].filter(Boolean).join(' ')}
      >
        {searchable ? (
          <div
            className={[
              styles.trigger,
              isMultiple && uniqueSelected.length > 0 ? styles.triggerWithChips : '',
              isMultiple && uniqueSelected.length === 1 ? styles.triggerWithOneChip : '',
              isTyping ? styles.triggerTyping : '',
            ].filter(Boolean).join(' ')}
            onClick={() => {
              if (isDisabled) return;
              setOpen(true);
              searchInputRef.current?.focus();
            }}
          >
            {isMultiple && uniqueSelected.length > 0 && (
              <span className={styles.chips}>
                {displayedSelected.map((item) => {
                  const value = getValue(item);
                  return (
                    <span key={String(value)} className={styles.chip}>
                      <span className={styles.chipLabel}>{getLabel(item, options)}</span>
                      <span
                        role="button"
                        tabIndex={0}
                        className={styles.chipRemove}
                        onClick={(event) => removeSelected(event, value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            removeSelected(event, value);
                          }
                        }}
                      >
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                          <path
                            d="M7.5 2.5L2.5 7.5M2.5 2.5L7.5 7.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                    </span>
                  );
                })}
                {remainingCount > 0 && (
                  <span className={`${styles.chip} ${styles.chipMore}`}>
                    <span className={styles.chipLabel}>{t('More')} +{remainingCount}</span>
                    <span className={styles.moreArrow} aria-hidden="true" />
                  </span>
                )}
              </span>
            )}
            <input
              ref={(node) => {
                searchInputRef.current = node;
                if (typeof triggerRef === 'function') triggerRef(node);
                else if (triggerRef) triggerRef.current = node;
              }}
              type="text"
              className={[
                styles.triggerInput,
                isMultiple && uniqueSelected.length > 0 ? styles.triggerInputCompact : '',
                !hasValue && !isTyping ? styles.triggerPlaceholder : '',
              ].filter(Boolean).join(' ')}
              value={triggerInputValue}
              placeholder={isMultiple && uniqueSelected.length > 0 ? '' : (placeholder || label)}
              disabled={isDisabled}
              aria-expanded={open}
              aria-haspopup="listbox"
              aria-autocomplete="list"
              onChange={handleTriggerInputChange}
              onFocus={handleTriggerInputFocus}
              onKeyDown={handleSearchKeyDown}
              onClick={(event) => event.stopPropagation()}
            />
            {((!isMultiple && hasValue) || uniqueSelected.length >= 2) && (
              <span
                role="button"
                tabIndex={0}
                className={styles.clearAll}
                aria-label={t('DeselectAll') || t('Clear') || 'Clear'}
                onClick={clearAll}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    clearAll(event);
                  }
                }}
              >
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                  <path
                    d="M7.5 2.5L2.5 7.5M2.5 2.5L7.5 7.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            )}
            <span className={styles.chevron} aria-hidden="true" />
          </div>
        ) : (
          <button
            type="button"
            ref={triggerRef}
            className={`${styles.trigger}${showAllSelected ? ` ${styles.triggerWrap}` : ''}`}
            disabled={isDisabled}
            aria-expanded={open}
            aria-haspopup="listbox"
            onClick={toggleOpen}
            onKeyDown={handleTriggerKeyDown}
          >
            {isMultiple && uniqueSelected.length > 0 ? (
              <span className={`${styles.chips}${showAllSelected ? ` ${styles.chipsWrap}` : ''}`}>
                {displayedSelected.map((item) => {
                  const value = getValue(item);
                  return (
                    <span key={String(value)} className={styles.chip}>
                      <span className={styles.chipLabel}>{getLabel(item, options)}</span>
                      <span
                        role="button"
                        tabIndex={0}
                        className={styles.chipRemove}
                        onClick={(event) => removeSelected(event, value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            removeSelected(event, value);
                          }
                        }}
                      >
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                          <path
                            d="M7.5 2.5L2.5 7.5M2.5 2.5L7.5 7.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                    </span>
                  );
                })}
                {remainingCount > 0 && (
                  <span className={`${styles.chip} ${styles.chipMore}`}>
                    <span className={styles.chipLabel}>{t('More')} +{remainingCount}</span>
                    <span className={styles.moreArrow} aria-hidden="true" />
                  </span>
                )}
              </span>
            ) : (
              <span
                className={[
                  styles.triggerText,
                  !isMultiple && !hasValue ? styles.triggerPlaceholder : '',
                ].filter(Boolean).join(' ')}
              >
                {singleLabel}
              </span>
            )}
            {clearable && !isMultiple && hasValue && (
              <span
                role="button"
                tabIndex={0}
                className={styles.clearAll}
                aria-label={t('DeselectAll') || t('Clear') || 'Clear'}
                onClick={clearAll}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    clearAll(event);
                  }
                }}
              >
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                  <path
                    d="M7.5 2.5L2.5 7.5M2.5 2.5L7.5 7.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            )}
            {isMultiple && uniqueSelected.length >= 2 && (
              <span
                role="button"
                tabIndex={0}
                className={styles.clearAll}
                aria-label={t('DeselectAll')}
                onClick={clearAll}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    clearAll(event);
                  }
                }}
              >
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                  <path
                    d="M7.5 2.5L2.5 7.5M2.5 2.5L7.5 7.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            )}
            <span className={styles.chevron} aria-hidden="true" />
          </button>
        )}

        {!portalMenu && listContent}
      </div>
      {portalMenu && listContent ? createPortal(listContent, document.body) : null}
    </div>
  );
};

export default AutocompleteSelectFilter;
